import React, { useContext, useMemo } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import TradingViewChart from "common/Charts/TradingViewChart";
import { TIME_RANGE_1_MONTHS } from "@shared/common/constants";
import { isSymbol } from "@shared/types/utils";
import styled, { useTheme } from "styled-components";
import { getIsReverse } from "@shared/features/bots/BotForm/common/utils";
import { getLineAnnotation } from "@shared/utils/charts";
import { getAnnotationDistanceData } from "@shared/features/trades/common/utils";
import {
  HedgingMode,
  StrategyOrder,
} from "@shared/features/bots/BotForm/common/types";
import { OrderTypes } from "@shared/features/trades/common/types";
import { BotType, LanguageNameSpaces } from "@shared/types";
import ExchangeSelectionDropdown from "features/portfolio/Assets/ExchangeSelectionDropdown.tsx";
import Flex from "common/Boxes/Flex.tsx";
import { useTradingViewChartExchangeSelector } from "features/tradingTerminal/common/hooks.ts";
import HighlightedText from "common/Text/HighlightedText.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { useDefaultExchangeSymbol } from "@shared/features/bots/BotForm/common/hooks.ts";

interface BotFormChartProps {
  isCard?: boolean;
}

const BotFormChart = ({ isCard }: BotFormChartProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const {
    botType,
    parsedEntries,
    symbols,
    account,
    symbolPriceData,
    longShort,
    parsedStop,
    parsedTakeProfits,
    exchanges,
    useSymbolFromAlert,
    hedgingMode,
  } = useContext(BotFormContext)!;
  const theme = useTheme();
  const symbol = isSymbol(symbols) ? undefined : symbols[0];
  const parsedExchanges = isSymbol(exchanges) ? undefined : exchanges;
  const accountExchange = isSymbol(account) ? undefined : account?.exchange;
  const reverse = getIsReverse(longShort);
  const currentPrice = symbolPriceData?.data;
  const isGridBot = botType === BotType.GRID;
  const isHedgingBot = botType === BotType.HEDGING;
  const isImmediateHedgingBot =
    isHedgingBot && hedgingMode === HedgingMode.FIXED;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;
  const isTradingViewBot =
    botType === BotType.TRADING_VIEW || isTradingViewSignalsBot;
  const { getDefaultSymbol } = useDefaultExchangeSymbol();

  const getOrderAnnotation = (
    orderType: OrderTypes,
    order: StrategyOrder,
    orderIndex: number,
    isLastOrder?: boolean
  ) => {
    const isEntry = orderType === OrderTypes.ENTRY;
    const isTakeProfit = orderType === OrderTypes.TAKE_PROFIT;
    const isStop = orderType === OrderTypes.STOP;
    const isFirstOrder = orderIndex === 0;
    let isBorderOrder = isFirstOrder || isLastOrder || isStop;
    if (isGridBot) {
      isBorderOrder =
        (isEntry && isLastOrder) || (isTakeProfit && isFirstOrder) || isStop;
    }
    let text = "";
    if (orderType === OrderTypes.STOP) text = "Stop";
    else if (isGridBot && isBorderOrder) {
      text = isLastOrder
        ? `${reverse ? "High" : "Low"} Price`
        : `${reverse ? "Low" : "High"} Price`;
      if (order.isPending) text = `${text} (Pending)`;
    }
    const color = isEntry
      ? theme.colors.green
      : isTakeProfit
      ? theme.colors.red
      : theme.colors.orange;
    const { distanceText } = getAnnotationDistanceData(
      order.price,
      currentPrice,
      symbolPriceData?.precision.price || 0
    );
    return getLineAnnotation({
      yMax: order.price,
      color,
      orderType,
      orderNumber: orderIndex + 1,
      isSimpleLine: !isBorderOrder,
      distance: isBorderOrder ? distanceText : undefined,
      text,
      isDashed: order.isPending,
    });
  };

  const annotations = useMemo(() => {
    if (!currentPrice || !parsedEntries) return [];
    if (isImmediateHedgingBot) return [];
    let annotations = parsedEntries.map((order, index) => {
      const isLastOrder = index === parsedEntries.length - 1;
      return getOrderAnnotation(OrderTypes.ENTRY, order, index, isLastOrder);
    });
    if (isHedgingBot) return annotations;
    annotations = annotations.concat(
      parsedTakeProfits?.map((order, index) => {
        const isLastOrder = index > 0 && index === parsedTakeProfits.length - 1;
        return getOrderAnnotation(
          OrderTypes.TAKE_PROFIT,
          order,
          index,
          isLastOrder
        );
      }) || []
    );
    if (parsedStop) {
      annotations.push(getOrderAnnotation(OrderTypes.STOP, parsedStop, 0));
    }
    return annotations;
  }, [
    isHedgingBot,
    isImmediateHedgingBot,
    parsedEntries,
    longShort,
    parsedStop,
    parsedTakeProfits,
  ]);

  const {
    symbolExchanges,
    setExchange,
    exchange: selectorExchange,
  } = useTradingViewChartExchangeSelector({
    exchanges: parsedExchanges,
    symbol,
  });
  const exchange = accountExchange ?? selectorExchange;
  const exchangeSymbol = useSymbolFromAlert
    ? getDefaultSymbol(exchange)
    : symbol;
  if (!exchangeSymbol || !exchange) return null;

  return (
    <ChartWrapper>
      {isTradingViewBot && (
        <HighlightedText style={{ margin: "8px 16px", padding: "8px 12px" }}>
          {t("messages.info.orders_for_illustration")}
        </HighlightedText>
      )}
      {isTradingViewSignalsBot && (
        <Flex $justifyContent="flex-end" style={{ marginRight: 16 }}>
          <ExchangeSelectionDropdown
            exchange={exchange}
            exchanges={symbolExchanges}
            setExchange={setExchange}
          />
        </Flex>
      )}
      <TradingViewChart
        symbol={exchangeSymbol}
        exchange={exchange}
        annotations={annotations}
        defaultTimeRange={TIME_RANGE_1_MONTHS}
        showExtendButton={true}
        isCard={isCard}
      />
    </ChartWrapper>
  );
};

export default BotFormChart;

const ChartWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;
